import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { ComposableMap, Geographies, Geography, Marker, Line, ZoomableGroup } from 'react-simple-maps';

import './TravelPinboardPage.css';
import { FetchAllCountries, FetchCountryName, ParseCoordinates } from './Utils/CountryUtility';
import {
  MAX_COUNTRY_SCOPE,
  MAX_USER_ZOOM,
  MAX_PROJECTION_SCALE,
  MIN_USER_ZOOM,
  ZOOM_BUTTON_STEP,
  ZOOM_SLIDER_STEP,
  isCountryAnyPost,
  normalizeCountryName,
  getSessionDefaultScope as computeSessionDefaultScope,
  getCountryCentersByIso,
  getCountryAnyPosts,
  getRouteLineSegments,
  getSelectedCountriesBounds,
  createSafeFileTimestamp,
  resolveCssVariables,
  downloadBlob,
  getMapProjectionConfig,
} from './Utils/TravelPinboardUtility';
import {
  fetchAllSightseeingForIso,
} from './Pinboard/PinboardRequests';
import {
  PINBOARD_ROUTE_COOKIE,
  PINBOARD_PINNED_COOKIE,
  PINBOARD_SCOPE_COOKIE,
  PINBOARD_SELECTED_ISO_COOKIE,
  parseNumericArrayCookie,
  parseIsoArrayCookie,
  parseSingleIsoCookie,
  writeCookieValue,
  persistRouteCookie,
  persistPinnedCookie,
  persistScopeCookie,
} from './Pinboard/PinboardStorage';
import {
  createMapAndListPngBlob,
  shareOrDownloadPinboardImage,
  waitForMapRenderReady,
  waitForDoubleAnimationFrame,
} from './Pinboard/PinboardExport';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const geoJson = '/assets/maps/world-50m.json';

function TravelPinboardPage(): JSX.Element {
  const [posts, setPosts] = useState<any[]>([]);
  const [routePostIds, setRoutePostIds] = useState<number[]>([]);
  const [pinnedPostIds, setPinnedPostIds] = useState<number[]>([]);
  const [selectedCountryIso, setSelectedCountryIso] = useState<string>('');
  const [selectedCountryIsos, setSelectedCountryIsos] = useState<string[]>([]);
  const [allCountryOptions, setAllCountryOptions] = useState<string[]>([]);
  const [countryNamesByIso, setCountryNamesByIso] = useState<Record<string, string>>({});
  const [countryScopeSearchText, setCountryScopeSearchText] = useState<string>('');
  const [isCountryScopeSearchFocused, setIsCountryScopeSearchFocused] = useState<boolean>(false);
  const [postSearchText, setPostSearchText] = useState<string>('');
  const [isPostSearchFocused, setIsPostSearchFocused] = useState<boolean>(false);
  const [pendingSightseeingPost, setPendingSightseeingPost] = useState<any | null>(null);
  const [hoveredPinPost, setHoveredPinPost] = useState<any | null>(null);
  const [hoverPreviewPosition, setHoverPreviewPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [userZoom, setUserZoom] = useState<number>(1);
  const [mapCenter, setMapCenter] = useState<[number, number]>([0, 0]);
  const [mapResetKey, setMapResetKey] = useState<number>(0);
  const [mapViewport, setMapViewport] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [geoTopology, setGeoTopology] = useState<any>(null);
  const [hasLoadedPosts, setHasLoadedPosts] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isSharing, setIsSharing] = useState<boolean>(false);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const hasSeededInitialRouteRef = useRef<boolean>(false);
  const sessionDefaultScopeRef = useRef<string[]>([]);
  const [hasCheckedStoredPinboardRoute, setHasCheckedStoredPinboardRoute] = useState<boolean>(false);
  const pinchStartDistanceRef = useRef<number | null>(null);
  const pinchStartZoomRef = useRef<number>(1);
  const isPinchingRef = useRef<boolean>(false);


  const getTouchDistance = (firstTouch: React.Touch, secondTouch: React.Touch): number => {
    const deltaX = secondTouch.clientX - firstTouch.clientX;
    const deltaY = secondTouch.clientY - firstTouch.clientY;
    return Math.hypot(deltaX, deltaY);
  };

  const handleMapTouchStart = (event: React.TouchEvent<HTMLDivElement>): void => {
    if (event.touches.length < 2) return;

    const firstTouch = event.touches[0];
    const secondTouch = event.touches[1];
    const startDistance = getTouchDistance(firstTouch, secondTouch);
    if (!Number.isFinite(startDistance) || startDistance <= 0) return;

    pinchStartDistanceRef.current = startDistance;
    pinchStartZoomRef.current = userZoom;
    isPinchingRef.current = true;
    event.preventDefault();
  };

  const handleMapTouchMove = (event: React.TouchEvent<HTMLDivElement>): void => {
    if (!isPinchingRef.current || event.touches.length < 2 || !pinchStartDistanceRef.current) return;

    const firstTouch = event.touches[0];
    const secondTouch = event.touches[1];
    const currentDistance = getTouchDistance(firstTouch, secondTouch);
    if (!Number.isFinite(currentDistance) || currentDistance <= 0) return;

    const zoomRatio = currentDistance / pinchStartDistanceRef.current;
    const nextZoom = Math.max(MIN_USER_ZOOM, Math.min(MAX_USER_ZOOM, pinchStartZoomRef.current * zoomRatio));
    setUserZoom(Number(nextZoom.toFixed(2)));
    event.preventDefault();
  };

  const handleMapTouchEnd = (event: React.TouchEvent<HTMLDivElement>): void => {
    if (event.touches.length >= 2) return;

    isPinchingRef.current = false;
    pinchStartDistanceRef.current = null;
  };

  useEffect(() => {
    FetchAllCountries()
      .then((countries) => {
        const namesByIso: Record<string, string> = {};
        countries.forEach((country) => {
          const iso = (country.iso || '').toUpperCase();
          const name = String(country.name || '').trim();
          if (iso.length === 2 && name) {
            namesByIso[iso] = name;
          }
        });

        const isos = countries
          .map((country) => (country.iso || '').toUpperCase())
          .filter((iso) => iso.length === 2)
          .sort((a, b) => a.localeCompare(b));
        setAllCountryOptions([...new Set(isos)]);
        setCountryNamesByIso((previous) => ({ ...previous, ...namesByIso }));
      })
      .catch(() => setAllCountryOptions([]));
  }, []);

  useEffect(() => {
    setUserZoom(1);
  }, [selectedCountryIsos]);

  useEffect(() => {
    fetch(geoJson)
      .then((response) => response.json())
      .then((data) => setGeoTopology(data))
      .catch((error) => {
        console.error('Error loading world topology:', error);
        setGeoTopology(null);
      });
  }, []);

  useEffect(() => {
    const updateViewport = () => {
      const width = mapContainerRef.current?.clientWidth || 0;
      const height = mapContainerRef.current?.clientHeight || 0;
      setMapViewport((previous) => {
        if (previous.width === width && previous.height === height) return previous;
        return { width, height };
      });
    };

    const rafId = window.requestAnimationFrame(updateViewport);
    const container = mapContainerRef.current;
    const resizeObserver = container ? new ResizeObserver(() => updateViewport()) : null;
    if (container && resizeObserver) {
      resizeObserver.observe(container);
    }

    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);

    return () => {
      window.cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
    };
  }, []);

  const togglePinnedList = (post: any): void => {
    const normalizedPostId = Number(post?.id);
    if (!Number.isFinite(normalizedPostId) || normalizedPostId === 0) return;

    setRoutePostIds((previousRouteIds) => {
      if (previousRouteIds.includes(normalizedPostId)) return previousRouteIds;
      const nextRouteIds = [...previousRouteIds, normalizedPostId];
      persistRouteCookie(nextRouteIds);
      return nextRouteIds;
    });

    setPinnedPostIds((prevIds) => {
      const isPinned = prevIds.includes(normalizedPostId);
      const nextPinnedIds = isPinned ? prevIds.filter((id) => id !== normalizedPostId) : [...prevIds, normalizedPostId];
      persistPinnedCookie(nextPinnedIds);
      return nextPinnedIds;
    });
  };

  const togglePinnedFromList = (postId: number): void => {
    setPinnedPostIds((previousPinnedIds) => {
      const nextPinnedIds = previousPinnedIds.includes(postId) ? previousPinnedIds.filter((id) => id !== postId) : [...previousPinnedIds, postId];
      persistPinnedCookie(nextPinnedIds);
      return nextPinnedIds;
    });
  };

  const removeFromRouteList = (postId: number): void => {
    setRoutePostIds((previousRouteIds) => {
      const nextRouteIds = previousRouteIds.filter((id) => id !== postId);
      persistRouteCookie(nextRouteIds);
      return nextRouteIds;
    });

    setPinnedPostIds((previousPinnedIds) => {
      const nextPinnedIds = previousPinnedIds.filter((id) => id !== postId);
      persistPinnedCookie(nextPinnedIds);
      return nextPinnedIds;
    });
  };

  const moveRouteItem = (postId: number, direction: 'up' | 'down'): void => {
    setRoutePostIds((previousRouteIds) => {
      const currentIndex = previousRouteIds.indexOf(postId);
      if (currentIndex < 0) return previousRouteIds;

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= previousRouteIds.length) return previousRouteIds;

      const nextRouteIds = [...previousRouteIds];
      [nextRouteIds[currentIndex], nextRouteIds[targetIndex]] = [nextRouteIds[targetIndex], nextRouteIds[currentIndex]];
      persistRouteCookie(nextRouteIds);
      return nextRouteIds;
    });
  };

  const canonicalUrl = 'https://livingoutofabackpack.com/travelpinboard/';
  const seoTitle = 'World Travel Pinboard | Living Out Of A Backpack';
  const seoDescription = 'Build a world travel pinboard route across countries.';
  const countryOptions = useMemo(
    () => [...new Set(posts.map((post) => (post.iso || '').toUpperCase()).filter((iso) => iso.length > 0))].sort((a, b) => a.localeCompare(b)),
    [posts],
  );
  const availableCountryOptions = allCountryOptions.length > 0 ? allCountryOptions : countryOptions;

  const countryScopeSearchOptions = useMemo(() => {
    const query = countryScopeSearchText.trim().toLowerCase();

    return availableCountryOptions
      .map((iso) => ({
        iso,
        name: countryNamesByIso[iso] || iso,
      }))
      .filter((option) => {
        if (!query) return true;
        return option.iso.toLowerCase().includes(query) || option.name.toLowerCase().includes(query);
      })
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 12);
  }, [availableCountryOptions, countryNamesByIso, countryScopeSearchText]);

  const resolveCountryScopeInputToIso = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return '';

    const upper = trimmed.toUpperCase();
    if (availableCountryOptions.includes(upper)) {
      return upper;
    }

    const lower = trimmed.toLowerCase();
    const isoByName = availableCountryOptions.find((iso) => (countryNamesByIso[iso] || '').toLowerCase() === lower);
    return isoByName || '';
  };

  const getSessionDefaultScope = (options: string[]): string[] => {
    const nextScope = computeSessionDefaultScope(options, sessionDefaultScopeRef.current, MAX_COUNTRY_SCOPE);
    sessionDefaultScopeRef.current = nextScope;
    return nextScope;
  };

  useEffect(() => {
    const storedRouteIds = parseNumericArrayCookie(PINBOARD_ROUTE_COOKIE);
    const storedPinnedIds = parseNumericArrayCookie(PINBOARD_PINNED_COOKIE);
    const storedScopeIsos = parseIsoArrayCookie(PINBOARD_SCOPE_COOKIE);
    const storedSelectedIso = parseSingleIsoCookie(PINBOARD_SELECTED_ISO_COOKIE);

    if (storedScopeIsos && storedScopeIsos.length > 0) {
      const limitedScope = storedScopeIsos.slice(0, MAX_COUNTRY_SCOPE);
      sessionDefaultScopeRef.current = limitedScope;
      setSelectedCountryIsos(limitedScope);
      setSelectedCountryIso(storedSelectedIso && limitedScope.includes(storedSelectedIso) ? storedSelectedIso : limitedScope[0]);
    } else if (storedSelectedIso) {
      setSelectedCountryIso(storedSelectedIso);
    }

    const hasStoredRoute = Boolean(storedRouteIds && storedRouteIds.length > 0);
    const hasStoredPinned = Boolean(storedPinnedIds && storedPinnedIds.length > 0);

    if (hasStoredRoute) {
      setRoutePostIds(storedRouteIds || []);
    }

    if (hasStoredPinned) {
      setPinnedPostIds(storedPinnedIds || []);
    }

    if (hasStoredRoute || hasStoredPinned) {
      hasSeededInitialRouteRef.current = true;
    }

    setHasCheckedStoredPinboardRoute(true);
  }, []);

  useEffect(() => {
    if (!hasCheckedStoredPinboardRoute) return;
    writeCookieValue(PINBOARD_ROUTE_COOKIE, JSON.stringify(routePostIds));
  }, [routePostIds, hasCheckedStoredPinboardRoute]);

  useEffect(() => {
    if (!hasCheckedStoredPinboardRoute) return;
    writeCookieValue(PINBOARD_PINNED_COOKIE, JSON.stringify(pinnedPostIds));
  }, [pinnedPostIds, hasCheckedStoredPinboardRoute]);

  useEffect(() => {
    if (!hasCheckedStoredPinboardRoute) return;
    writeCookieValue(PINBOARD_SCOPE_COOKIE, JSON.stringify(selectedCountryIsos));
  }, [selectedCountryIsos, hasCheckedStoredPinboardRoute]);

  useEffect(() => {
    if (!hasCheckedStoredPinboardRoute) return;
    if (!selectedCountryIso) return;
    writeCookieValue(PINBOARD_SELECTED_ISO_COOKIE, selectedCountryIso.toUpperCase());
  }, [selectedCountryIso, hasCheckedStoredPinboardRoute]);

  useEffect(() => {
    if (availableCountryOptions.length === 0) {
      return;
    }

    setSelectedCountryIsos((previous) => {
      const filtered = previous.filter((iso) => availableCountryOptions.includes(iso)).slice(0, MAX_COUNTRY_SCOPE);
      const defaultScope = getSessionDefaultScope(availableCountryOptions);
      let nextScope: string[];

      if (filtered.length > 0) {
        const isDefaultSubset = filtered.every((iso) => defaultScope.includes(iso));
        if (isDefaultSubset && defaultScope.length > 0) {
          nextScope = [...new Set([...filtered, ...defaultScope])].slice(0, MAX_COUNTRY_SCOPE);
        } else {
          nextScope = filtered;
        }
      } else if (defaultScope.length > 0) {
        nextScope = defaultScope.slice(0, MAX_COUNTRY_SCOPE);
      } else {
        nextScope = [availableCountryOptions[0]];
      }

      setSelectedCountryIso((previousIso) => {
        if (previousIso && availableCountryOptions.includes(previousIso)) {
          return previousIso;
        }
        if (nextScope.length > 0 && availableCountryOptions.includes(nextScope[0])) {
          return nextScope[0];
        }
        return availableCountryOptions[0];
      });

      const isSameLength = previous.length === nextScope.length;
      const isSameValues = isSameLength && previous.every((iso, index) => iso === nextScope[index]);
      return isSameValues ? previous : nextScope;
    });
  }, [availableCountryOptions]);

  useEffect(() => {
    if (availableCountryOptions.length === 0) return;
    if (selectedCountryIso) return;
    if (selectedCountryIsos.length > 0) {
      setSelectedCountryIso(selectedCountryIsos[0]);
      return;
    }
    const defaultScope = getSessionDefaultScope(availableCountryOptions);
    setSelectedCountryIso(defaultScope[0] || availableCountryOptions[0]);
  }, [availableCountryOptions, selectedCountryIso, selectedCountryIsos]);

  useEffect(() => {
    if (!selectedCountryIso) return;
    if (isCountryScopeSearchFocused) return;
    setCountryScopeSearchText(countryNamesByIso[selectedCountryIso] || selectedCountryIso);
  }, [selectedCountryIso, countryNamesByIso, isCountryScopeSearchFocused]);

  useEffect(() => {
    if (selectedCountryIsos.length === 0) {
      setPosts([]);
      setHasLoadedPosts(true);
      return;
    }

    const controller = new AbortController();
    let isCancelled = false;

    setHasLoadedPosts(false);

    Promise.all(selectedCountryIsos.map((iso) => fetchAllSightseeingForIso(backendUrl, iso, controller.signal)))
      .then((results) => {
        if (isCancelled) return;

        const merged = results.flat();
        const uniqueById = new Map<number, any>();

        merged.forEach((post) => {
          const id = Number(post?.id);
          if (Number.isFinite(id) && id > 0) {
            uniqueById.set(id, post);
          }
        });

        setPosts(Array.from(uniqueById.values()));
      })
      .catch((error) => {
        if (isCancelled) return;
        if (error?.name === 'CanceledError') return;

        console.error('Error fetching scoped sightseeing data:', error);
        setPosts([]);
      })
      .finally(() => {
        if (!isCancelled) {
          setHasLoadedPosts(true);
        }
      });

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [selectedCountryIsos]);

  useEffect(() => {
    const missingIsos = selectedCountryIsos.filter((iso) => !countryNamesByIso[iso]);
    if (missingIsos.length === 0) return;

    Promise.all(
      missingIsos.map(async (iso) => {
        try {
          const countryName = await FetchCountryName(iso);
          return [iso, countryName || ''] as const;
        } catch {
          return [iso, ''] as const;
        }
      }),
    ).then((results) => {
      setCountryNamesByIso((previous) => {
        const next = { ...previous };
        results.forEach(([iso, name]) => {
          next[iso] = name;
        });
        return next;
      });
    });
  }, [selectedCountryIsos, countryNamesByIso]);

  const countryCentersByIso = useMemo(() => {
    return getCountryCentersByIso(geoTopology, availableCountryOptions, countryNamesByIso);
  }, [geoTopology, countryNamesByIso, availableCountryOptions]);

  const countryAnyPosts = useMemo(
    () => getCountryAnyPosts(selectedCountryIsos, countryCentersByIso, countryNamesByIso),
    [selectedCountryIsos, countryCentersByIso, countryNamesByIso],
  );

  const seedPinBoard = useCallback((): void => {
    if (!hasCheckedStoredPinboardRoute) return;
    if (hasSeededInitialRouteRef.current) return;
    if (routePostIds.length > 0 || pinnedPostIds.length > 0) {
      hasSeededInitialRouteRef.current = true;
      return;
    }

    if (!hasLoadedPosts) return;

    const selectedIsoSet = new Set(selectedCountryIsos.filter((iso) => availableCountryOptions.includes(iso)));
    if (selectedIsoSet.size === 0) return;

    const specificCandidates = posts.filter(
      (post) =>
        !isCountryAnyPost(post) &&
        Number(post?.id) > 0 &&
        selectedIsoSet.has(String(post?.iso || '').toUpperCase()) &&
        (() => {
          const [lat, lon] = ParseCoordinates(post?.coordinates || '');
          return !Number.isNaN(lat) && !Number.isNaN(lon);
        })(),
    );

    const shuffledSpecifics = [...specificCandidates].sort(() => Math.random() - 0.5);
    const specificIds = [...new Set(
      shuffledSpecifics
      .slice(0, 2)
      .map((post) => Number(post.id))
      .filter((id): id is number => Number.isFinite(id) && id > 0),
    )];

    const anyCandidates = countryAnyPosts.filter((post) => selectedIsoSet.has(String(post?.iso || '').toUpperCase()));
    const anyIds = [...new Set(
      anyCandidates
      .slice(0, 2)
      .map((post) => Number(post.id))
      .filter((id): id is number => Number.isFinite(id) && id !== 0),
    )];

    const initialSeedIds = specificIds.length >= 2 ? specificIds : anyIds;
    if (initialSeedIds.length === 0) return;

    setRoutePostIds((previousRouteIds) => (previousRouteIds.length === 0 ? initialSeedIds : previousRouteIds));
    setPinnedPostIds((previousPinnedIds) => (previousPinnedIds.length === 0 ? initialSeedIds : previousPinnedIds));

    hasSeededInitialRouteRef.current = true;
  }, [countryAnyPosts, posts, hasLoadedPosts, routePostIds.length, pinnedPostIds.length, selectedCountryIsos, availableCountryOptions, hasCheckedStoredPinboardRoute]);

  // On first load, seed with 2 random specific sightseeing posts.
  // If fewer than 2 specific posts are available, fall back to 2 "Any" country pins.
  useEffect(() => {
    seedPinBoard();
  }, [seedPinBoard]);

  const pinPosts = useMemo(() => {
    const sightseeingPinPosts = posts
      .map((post) => {
        const [lat, lon] = ParseCoordinates(post.coordinates || '');
        const normalizedId = Number(post?.id);
        return {
          ...post,
          id: normalizedId,
          pinCoordinates: [lon, lat] as [number, number],
        };
      })
      .filter((post) => Number.isFinite(post.id) && !Number.isNaN(post.pinCoordinates[0]) && !Number.isNaN(post.pinCoordinates[1]));

    return [...sightseeingPinPosts, ...countryAnyPosts];
  }, [posts, countryAnyPosts]);

  const visiblePinPosts = useMemo(() => {
    if (selectedCountryIsos.length === 0) return [];
    const selectedIsoSet = new Set(selectedCountryIsos);
    return pinPosts.filter((post) => selectedIsoSet.has((post.iso || '').toUpperCase()));
  }, [pinPosts, selectedCountryIsos]);

  const sightseeingSearchOptions = useMemo(() => {
    const query = postSearchText.trim().toLowerCase();
    return visiblePinPosts
      .filter((post) => String(post.place || post.activity || '').toLowerCase().includes(query))
      .sort((a, b) => {
        const aIsAny = isCountryAnyPost(a);
        const bIsAny = isCountryAnyPost(b);
        if (aIsAny && !bIsAny) return -1;
        if (!aIsAny && bIsAny) return 1;
        return String(a.place || a.activity || '').localeCompare(String(b.place || b.activity || ''));
      });
  }, [visiblePinPosts, postSearchText]);

  const resolvedSightseeingSelection = useMemo(() => {
    if (pendingSightseeingPost?.id) return pendingSightseeingPost;

    const query = postSearchText.trim().toLowerCase();
    if (!query) return null;

    return (
      visiblePinPosts.find((post) => String(post.place || post.activity || '').trim().toLowerCase() === query) ||
      null
    );
  }, [pendingSightseeingPost, postSearchText, visiblePinPosts]);

  const pinPostsById = new Map(visiblePinPosts.map((post) => [Number(post.id), post]));
  const routePosts = routePostIds
    .map((id) => pinPostsById.get(id))
    .filter((post): post is any => Boolean(post));
  const pinnedPostIdSet = new Set(pinnedPostIds);
  const pinnedPinPosts = routePosts.filter((post) => pinnedPostIdSet.has(post.id));

  const normalizedSelectedCountryNames = useMemo(
    () => new Set(selectedCountryIsos.map((iso) => normalizeCountryName(countryNamesByIso[iso] || '')).filter((name) => Boolean(name))),
    [selectedCountryIsos, countryNamesByIso],
  );
  const selectedCountriesBounds = useMemo(() => {
    return getSelectedCountriesBounds(geoTopology, normalizedSelectedCountryNames);
  }, [normalizedSelectedCountryNames, geoTopology]);

  const addCountryToScope = (): void => {
    if (!selectedCountryIso || selectedCountryIsos.includes(selectedCountryIso) || selectedCountryIsos.length >= MAX_COUNTRY_SCOPE) return;
    setSelectedCountryIsos((previous) => {
      const nextScopeIsos = [...previous, selectedCountryIso];
      persistScopeCookie(nextScopeIsos);
      return nextScopeIsos;
    });
  };

  const removeCountryFromScope = (iso: string): void => {
    setSelectedCountryIsos((previous) => {
      const nextScopeIsos = previous.filter((item) => item !== iso);
      persistScopeCookie(nextScopeIsos);
      return nextScopeIsos;
    });
  };

  const addSightseeingToScope = (): void => {
    const resolvedPost = resolvedSightseeingSelection;
    const normalizedPostId = Number(resolvedPost?.id);
    if (!Number.isFinite(normalizedPostId) || normalizedPostId === 0) return;

    setRoutePostIds((previousRouteIds) => {
      if (previousRouteIds.includes(normalizedPostId)) return previousRouteIds;
      const nextRouteIds = [...previousRouteIds, normalizedPostId];
      persistRouteCookie(nextRouteIds);
      return nextRouteIds;
    });

    setPinnedPostIds((previousPinnedIds) => {
      if (previousPinnedIds.includes(normalizedPostId)) return previousPinnedIds;
      const nextPinnedIds = [...previousPinnedIds, normalizedPostId];
      persistPinnedCookie(nextPinnedIds);
      return nextPinnedIds;
    });

    setPostSearchText('');
    setPendingSightseeingPost(null);
    setIsPostSearchFocused(false);
  };

  const resetMapForCapture = async (): Promise<void> => {
    setUserZoom(1);
    setMapCenter(mapProjectionConfig.center);
    setMapResetKey((previous) => previous + 1);

    await waitForDoubleAnimationFrame();
  };

  const exportMapAndList = async (): Promise<void> => {
    if (isExporting || isSharing) return;

    setIsExporting(true);

    try {
      await resetMapForCapture();
      await waitForMapRenderReady(mapContainerRef.current, selectedCountryIsos.length);
      const pngBlob = await createMapAndListPngBlob({
        mapContainer: mapContainerRef.current,
        routePosts,
        pinnedPostIdSet,
        backendUrl,
        resolveCssVariables,
      });
      const timestamp = createSafeFileTimestamp();
      downloadBlob(pngBlob, `travel-pinboard-map-${timestamp}.png`);
    } catch (error) {
      console.error('Error exporting pinboard map and list:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const shareMapAndList = async (): Promise<void> => {
    if (isSharing || isExporting) return;

    setIsSharing(true);

    try {
      await resetMapForCapture();
      await waitForMapRenderReady(mapContainerRef.current, selectedCountryIsos.length);
      const pngBlob = await createMapAndListPngBlob({
        mapContainer: mapContainerRef.current,
        routePosts,
        pinnedPostIdSet,
        backendUrl,
        resolveCssVariables,
      });
      const timestamp = createSafeFileTimestamp();
      const fileName = `travel-pinboard-map-${timestamp}.png`;
      const shareTitle = 'My Travel Pinboard';
      const shareText = 'Check out my travel pinboard route.';
      const shareUrl = canonicalUrl;

      await shareOrDownloadPinboardImage(
        pngBlob,
        fileName,
        shareTitle,
        shareText,
        shareUrl,
        downloadBlob,
      );
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        console.error('Error sharing pinboard map and list:', error);
      }
    } finally {
      setIsSharing(false);
    }
  };

  const mapProjectionConfig = useMemo(
    () =>
      getMapProjectionConfig({
        mapViewport,
        userZoom,
        selectedCountryIsos,
        pinnedPinPosts,
        visiblePinPosts,
        selectedCountriesBounds,
        maxProjectionScale: MAX_PROJECTION_SCALE,
      }),
    [selectedCountryIsos, pinnedPinPosts, visiblePinPosts, selectedCountriesBounds, userZoom, mapViewport],
  );

  useEffect(() => {
    setMapCenter((previousCenter) => {
      if (previousCenter[0] === mapProjectionConfig.center[0] && previousCenter[1] === mapProjectionConfig.center[1]) {
        return previousCenter;
      }
      return mapProjectionConfig.center;
    });
  }, [mapProjectionConfig.center]);

  return (
    <div className="TravelPinboardPage">
      <Helmet>
        <title>{seoTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
      </Helmet>

      <section className="PinboardPageHeader" aria-label="Pinboard page heading">
        <h1 className="PinboardPageTitle">World Pinboard 🌍</h1>
        <h2 className="PinboardPageSubtitle">Add sightseeing to your route, then select pins to show on the map.</h2>
      </section>

      <section className="PinboardFilterSection" aria-label="Pinboard country filter">
        <div className="PinboardFilterRow">
          <label htmlFor="pinboard-country-filter">Country Scope</label>
          <div className="PinboardSearchWrap">
            <input
              id="pinboard-country-filter"
              className="PinboardSearchInput"
              type="text"
              placeholder="Search country name"
              value={countryScopeSearchText}
              onFocus={() => setIsCountryScopeSearchFocused(true)}
              onBlur={() => {
                window.setTimeout(() => setIsCountryScopeSearchFocused(false), 100);
              }}
              onChange={(event) => {
                const nextValue = event.target.value;
                setCountryScopeSearchText(nextValue);
                const resolvedIso = resolveCountryScopeInputToIso(nextValue);
                if (resolvedIso) {
                  setSelectedCountryIso(resolvedIso);
                }
              }}
            />
            {isCountryScopeSearchFocused && countryScopeSearchOptions.length > 0 && (
              <div className="PinboardSearchSuggestions" role="listbox" aria-label="Country suggestions">
                {countryScopeSearchOptions.map((option) => (
                  <button
                    key={`country-filter-${option.iso}`}
                    type="button"
                    className="PinboardSearchSuggestionItem"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      setCountryScopeSearchText(option.name);
                      setSelectedCountryIso(option.iso);
                      setIsCountryScopeSearchFocused(false);
                    }}
                  >
                    <span className="PinboardSearchSuggestionThumb">
                      <img src={`https://flagcdn.com/w320/${option.iso.toLowerCase()}.png`} alt={`${option.name} flag`} loading="lazy" decoding="async" />
                    </span>
                    <span className="PinboardSearchSuggestionText">{option.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className="PinboardCountryAddButton"
            onClick={addCountryToScope}
            disabled={!selectedCountryIso || selectedCountryIsos.includes(selectedCountryIso) || selectedCountryIsos.length >= MAX_COUNTRY_SCOPE}
          >
            Add Country
          </button>
          <div className="PinboardSelectedCountries" aria-label="Selected countries">
            <span className="PinboardSelectedCountriesTitle">
              Countries {selectedCountryIsos.length}/{MAX_COUNTRY_SCOPE}
            </span>
            {selectedCountryIsos.map((iso) => (
              <span key={`selected-country-${iso}`} className="PinboardCountryBadge">
                <span>{iso}</span>
                <img src={`https://flagcdn.com/w320/${iso.toLowerCase()}.png`} alt={`${iso} flag`} />
                <button type="button" className="PinboardCountryChipRemove" onClick={() => removeCountryFromScope(iso)} aria-label={`Remove ${iso}`}>
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="PinboardFilterRow">
          <label htmlFor="pinboard-post-search">Sightseeing</label>
          <div className="PinboardSearchWrap">
            <input
              id="pinboard-post-search"
              className="PinboardSearchInput"
              type="text"
              placeholder="Search sightseeing name"
              value={postSearchText}
              onFocus={() => setIsPostSearchFocused(true)}
              onBlur={() => {
                window.setTimeout(() => setIsPostSearchFocused(false), 100);
              }}
              onChange={(event) => {
                setPostSearchText(event.target.value);
                setPendingSightseeingPost(null);
              }}
            />
            {isPostSearchFocused && sightseeingSearchOptions.length > 0 && (
              <div className="PinboardSearchSuggestions" role="listbox" aria-label="Sightseeing suggestions">
                {sightseeingSearchOptions.map((post) => {
                  const optionName = post.place || post.activity || `Post ${post.id}`;
                  return (
                    <button
                      key={`pinboard-sightseeing-option-${post.id}`}
                      type="button"
                      className="PinboardSearchSuggestionItem"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        setPostSearchText(optionName);
                        setPendingSightseeingPost(post);
                        setIsPostSearchFocused(false);
                      }}
                    >
                      <span className="PinboardSearchSuggestionThumb">
                        <img
                          src={isCountryAnyPost(post) ? `https://flagcdn.com/w320/${String(post.iso || '').toLowerCase()}.png` : `${backendUrl}/django-dynamic/pictures/${post.images?.[0]}`}
                          alt={optionName}
                          loading="lazy"
                          decoding="async"
                        />
                      </span>
                      <span className="PinboardSearchSuggestionText">{optionName}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button
            type="button"
            className="PinboardCountryAddButton"
            onClick={addSightseeingToScope}
              disabled={
                !Number.isFinite(Number(resolvedSightseeingSelection?.id)) || Number(resolvedSightseeingSelection?.id) === 0 ||
                routePostIds.includes(Number(resolvedSightseeingSelection?.id))
              }
          >
            Add Sightseeing
          </button>
          <div className="PinboardSelectedSightseeing" aria-label="Selected sightseeing posts">
            <span className="PinboardSelectedSightseeingTitle">Sightseeing {routePosts.length}</span>
            {routePosts.map((post) => {
              const label = post.place || post.activity || `Post ${post.id}`;
              return (
                <span key={`selected-sightseeing-${post.id}`} className="PinboardSightseeingBadge">
                  <span className="PinboardSightseeingBadgeText">{label}</span>
                  <button
                    type="button"
                    className="PinboardCountryChipRemove"
                    onClick={() => removeFromRouteList(post.id)}
                    aria-label={`Remove ${label}`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      </section>

      <section className="PersonaPinboardSection" aria-label="Persona pinboard map">
        <h3>
          {selectedCountryIsos.length === 1 ? `${selectedCountryIsos[0]} Pinboard` : 'Travel Pinboard'}
        </h3>
        <div className="PinboardZoomControls PinboardZoomControlsMap">
          <button type="button" className="PinboardZoomButton" onClick={() => setUserZoom((prev) => Math.max(MIN_USER_ZOOM, Number((prev - ZOOM_BUTTON_STEP).toFixed(2))))}>
            -
          </button>
          <div className="PinboardZoomSliderWrap" aria-label="Zoom level slider">
            <input
              type="range"
              className="PinboardZoomSlider"
              min={MIN_USER_ZOOM}
              max={MAX_USER_ZOOM}
              step={ZOOM_SLIDER_STEP}
              value={userZoom}
              onChange={(event) => setUserZoom(Number(event.target.value))}
            />
          </div>
          <button type="button" className="PinboardZoomButton" onClick={() => setUserZoom((prev) => Math.min(MAX_USER_ZOOM, Number((prev + ZOOM_BUTTON_STEP).toFixed(2))))}>
            +
          </button>
          <button
            type="button"
            className="PinboardZoomButton"
            onClick={() => {
              setUserZoom(1);
              setMapCenter(mapProjectionConfig.center);
              setMapResetKey((previous) => previous + 1);
            }}
          >
            Reset
          </button>
          <button type="button" className="PinboardZoomButton PinboardExportButton" onClick={exportMapAndList} disabled={isExporting}>
            {isExporting ? 'Exporting…' : 'Export'}
          </button>
          <button type="button" className="PinboardZoomButton PinboardShareButton" onClick={shareMapAndList} disabled={isSharing || isExporting}>
            {isSharing ? 'Sharing…' : 'Share'}
          </button>
        </div>
        <p className="PinboardExportHelp">Use Export to download PNG, or Share to send it via available apps on your device.</p>
        <div className="PinboardMapWithHint">
          <p className="map-gesture-hint">Zoom tip: use two fingers to pinch in or out on the map, or use the zoom buttons/slider.</p>
          <div
            ref={mapContainerRef}
            className="PersonaPinboardMap"
            role="img"
            aria-label={`${selectedCountryIsos.length === 0 ? 'World' : selectedCountryIsos.join(', ')} map with pinned travel posts`}
            style={{ touchAction: 'none' }}
            onTouchStart={handleMapTouchStart}
            onTouchMove={handleMapTouchMove}
            onTouchEnd={handleMapTouchEnd}
            onTouchCancel={handleMapTouchEnd}
          >
          <ComposableMap
            width={Math.max(1, Math.round(mapViewport.width || 900))}
            height={Math.max(1, Math.round(mapViewport.height || 450))}
            projectionConfig={mapProjectionConfig}
          >
            <ZoomableGroup
              key={`pinboard-zoom-group-${mapResetKey}`}
              center={mapCenter}
              zoom={1}
              onMoveEnd={(position: any) => {
                if (position?.coordinates) {
                  setMapCenter(position.coordinates as [number, number]);
                }
              }}
            >
              <Geographies geography={geoJson}>
                {({ geographies }) => {
                  const geographiesToRender =
                    normalizedSelectedCountryNames.size === 0
                      ? []
                      : geographies.filter((geo) => normalizedSelectedCountryNames.has(normalizeCountryName(String(geo.properties?.name || ''))));

                  return geographiesToRender.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      style={{
                        default: {
                          fill: 'var(--surface-alt)',
                          outline: 'none',
                          stroke: 'var(--text-color)',
                          strokeWidth: 0.2,
                        },
                        hover: {
                          fill: 'var(--surface-alt)',
                          outline: 'none',
                          stroke: 'var(--text-color)',
                          strokeWidth: 0.2,
                        },
                        pressed: {
                          fill: 'var(--surface-alt)',
                          outline: 'none',
                          stroke: 'var(--text-color)',
                          strokeWidth: 0.2,
                        },
                      }}
                    />
                  ));
                }}
              </Geographies>

              {pinnedPinPosts.slice(1).map((post, index) => {
                const previousPost = pinnedPinPosts[index];
                const segments = getRouteLineSegments(previousPost.pinCoordinates, post.pinCoordinates);

                return segments.map((segment, segmentIndex) => (
                  <Line
                    key={`pin-line-${post.id}-${segmentIndex}`}
                    from={segment.from}
                    to={segment.to}
                    stroke="var(--text-dim)"
                    strokeWidth={1.2}
                    strokeLinecap="round"
                    strokeDasharray="3 2"
                  />
                ));
              })}

              {pinnedPinPosts.map((post, index) => {
                return (
                  <Marker key={`pin-marker-${post.id}`} coordinates={post.pinCoordinates}>
                    <g
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                      aria-label={`Unpin ${post.place || post.activity || 'post'} from route`}
                      onClick={() => togglePinnedList(post)}
                      onMouseEnter={(event) => {
                        if (!isCountryAnyPost(post)) {
                          setHoveredPinPost(post);
                          setHoverPreviewPosition({ x: event.clientX, y: event.clientY });
                        }
                      }}
                      onMouseMove={(event) => {
                        if (!isCountryAnyPost(post)) {
                          setHoverPreviewPosition({ x: event.clientX, y: event.clientY });
                        }
                      }}
                      onMouseLeave={() => {
                        setHoveredPinPost(null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          togglePinnedList(post);
                        }
                      }}
                    >
                      <rect x={-8} y={-8} width={16} height={11} rx={2} ry={2} fill="var(--surface-elevated)" stroke="var(--primary-color)" strokeWidth={1.3} />
                      <image href={`https://flagcdn.com/w40/${String(post.iso || '').toLowerCase()}.png`} x={-7} y={-7} width={14} height={9} preserveAspectRatio="xMidYMid slice" />
                      <line x1={-8} y1={3.5} x2={-8} y2={9.5} stroke="var(--primary-color)" strokeWidth={1.3} strokeLinecap="round" />
                      <circle cx={8} cy={-9} r={4.2} fill="var(--surface-elevated)" stroke="var(--primary-color)" strokeWidth={1.1} />
                      <text x={8} y={-7.8} textAnchor="middle" fontSize={5} fontWeight={700} fill="var(--text-color)">
                        {index + 1}
                      </text>
                    </g>
                  </Marker>
                );
              })}
            </ZoomableGroup>
          </ComposableMap>
          {hoveredPinPost && (
            <div
              className="PinboardHoverPreview"
              style={{
                left: `${hoverPreviewPosition.x}px`,
                top: `${hoverPreviewPosition.y}px`,
              }}
            >
              <div className="PinboardHoverPreviewThumb">
                <img
                  src={`${backendUrl}/django-dynamic/pictures/${hoveredPinPost.images?.[0]}`}
                  alt={hoveredPinPost.place || hoveredPinPost.activity || 'Sightseeing preview'}
                />
              </div>
              <div className="PinboardHoverPreviewText">{hoveredPinPost.place || hoveredPinPost.activity || `Post ${hoveredPinPost.id}`}</div>
            </div>
          )}
          </div>
        </div>
      </section>

      <section className="PersonaPinnedList" aria-label="Pinned route order">
        <h3>Pinned Route Order</h3>
        {routePosts.length > 0 ? (
          <ol>
            {routePosts.map((post, index) => (
              <li key={`pinned-order-${post.id}`}>
                <input
                  type="checkbox"
                  className="PersonaPinnedToggle"
                  checked={pinnedPostIdSet.has(post.id)}
                  onChange={() => togglePinnedFromList(post.id)}
                  aria-label={`${pinnedPostIdSet.has(post.id) ? 'Unpin' : 'Pin'} ${post.place || post.activity || `Post ${post.id}`}`}
                />
                <span className="PersonaOrderNumber">#{index + 1}</span>
                <div className="PersonaPinnedThumb">
                  {isCountryAnyPost(post) ? (
                    <div className="PersonaPinnedThumbFallback" aria-label={`${post.iso} country pin`}>
                      <img src={`https://flagcdn.com/w320/${String(post.iso || '').toLowerCase()}.png`} alt={`${post.iso} flag`} />
                    </div>
                  ) : (
                    <img src={`${backendUrl}/django-dynamic/pictures/${post.images?.[0]}`} alt={post.place || post.activity || 'Pinned sightseeing post'} />
                  )}
                </div>
                <span className="PersonaPinnedItemText">{post.place || post.activity || `Post ${post.id}`}</span>
                <div className="PersonaPinnedItemActions">
                  <button type="button" className="PersonaPinnedActionButton" onClick={() => moveRouteItem(post.id, 'up')} disabled={index === 0}>
                    Up
                  </button>
                  <button
                    type="button"
                    className="PersonaPinnedActionButton"
                    onClick={() => moveRouteItem(post.id, 'down')}
                    disabled={index === routePosts.length - 1}
                  >
                    Down
                  </button>
                  <button type="button" className="PersonaPinnedActionButton PersonaRemoveButton" onClick={() => removeFromRouteList(post.id)}>
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <div className="PersonaPinnedEmpty">Click cards or pins to build your route order.</div>
        )}
      </section>
    </div>
  );
}

export default TravelPinboardPage;
